import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { Link, LinkType } from "~/common/components/Link";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { useLockBodyScroll } from "~/common/hooks/useLockBodyScroll";
import classNames from "classnames";
import classes from "./SearchRelatedResults.module.less";
import type { ContentDataItem, SrpProductData } from "~/utils/models";
import { useEffect, useMemo, useRef, useState } from "react";
import { createHighlightedText } from "~/features/search/components/Autosuggest/helpers";
import type { AutosuggestProductGroup } from "~/domains/navigation/types/NavigationTypes";
import type { SearchEntry } from "~/features/search/components/Autosuggest/RecentSearches/RecentSearches";
import SearchItem from "~/features/search/components/Autosuggest/SearchRelatedResults/SearchItem";
import { SEARCH_PAGE } from "~/utils/url/staticUrls";
import { getToolConfiguration } from "~/domains/tools/config/ToolConfig";
import { useTranslation } from "react-i18next";
import { saveSearchTerm } from "~/features/search/api/saveSearchTerm";
import { ProductItem } from "~/features/search/components/Autosuggest/SearchRelatedResults/ProductItem/ProductItem";
import { Skeleton } from "~/common/mui";

interface SearchRelatedResultsProps {
  className?: string | false;
  products?: SrpProductData[];
  suggestions?: SearchEntry[];
  groups?: AutosuggestProductGroup[];
  assets?: ContentDataItem[];
  state: "loading" | "loaded" | "error";
  query: string;
  hide: () => void;
}

const MAX_SUGGESTIONS = 4;
const MAX_SUGGESTIONS_TABLET = 3;
const MAX_GROUPS = 3;
const MAX_GROUPS_TABLET = 2;
const MAX_ASSETS = 10;
const MAX_ASSETS_TABLET = 8;
const MAX_PRODUCTS = 5;

enum ContentTypes {
  Suggestions,
  Groups,
  Assets,
}

const SearchRelatedResults = ({
  products,
  suggestions,
  query,
  groups,
  assets,
  state,
  hide,
  className,
}: SearchRelatedResultsProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  if (!isDesktop) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useLockBodyScroll();
  }

  const refList = useRef<HTMLAnchorElement[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex + 1) % refList.current.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex - 1 + refList.current.length) % refList.current.length);
      } else if (event.key === "Escape") {
        event.preventDefault();
        hide();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [refList]);

  useEffect(() => {
    if (focusedIndex !== -1) {
      refList.current[focusedIndex].focus();
    }
  }, [focusedIndex]);

  const configuratorTypes = [
    ...new Set(
      products
        ?.map((product) => product.configuratorTypes)
        .flatMap((toolType) => toolType)
        .map((toolType) => getToolConfiguration(toolType)),
    ),
  ];

  const contentWithHighlights = useMemo(() => {
    const assetsWithConfigurators = assets && [
      ...configuratorTypes.map((config) => ({
        title: config.name,
        label: config.url,
      })),
      ...assets,
    ];

    return {
      suggestions: suggestions?.slice(0, isDesktop ? MAX_SUGGESTIONS : MAX_SUGGESTIONS_TABLET).map((item) => ({
        ...item,
        highlight: createHighlightedText(item.term, query),
        term: item.term,
      })),

      groups: groups?.slice(0, isDesktop ? MAX_GROUPS : MAX_GROUPS_TABLET).map((item) => ({
        ...item,
        highlight: createHighlightedText(item.name ?? "", query),
        term: item.name,
      })),

      assets: assetsWithConfigurators?.slice(0, MAX_ASSETS).map((item) => {
        const translatedTitle = t(item.title);
        return {
          ...item,
          highlight: createHighlightedText(translatedTitle, query),
          term: translatedTitle,
        };
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, groups, assets]);

  const createLinkItem = (
    href: string,
    content: JSX.Element,
    state: "loading" | "loaded" | "error",
    key: number,
    term: string,
    type?: ContentTypes,
  ) => {
    return state === "loaded" ? (
      <Link
        key={key}
        className={classes.suggestionsLink}
        variant={LinkType.SECONDARY}
        type="button"
        onClick={() => saveSearchTerm(term, href)}
        href={href}
        ref={(el) => {
          if (el && !refList.current.includes(el)) {
            refList.current.push(el);
          }
        }}
        {...(type === ContentTypes.Groups && { "tracking-aux-sch-prodgroup-name": term })}
        {...(type === ContentTypes.Assets && { "tracking-aux-sch-content-name": term })}
      >
        {content}
      </Link>
    ) : (
      <Skeleton key={key} variant="rectangular" />
    );
  };

  const dynamicMaxAssets =
    (isDesktop ? MAX_ASSETS : MAX_ASSETS_TABLET) -
    (contentWithHighlights?.suggestions?.length ?? 0) -
    (contentWithHighlights?.groups?.length ?? 0);

  return (
    <div className={classNames(className, classes.root, classes.searchRelatedResultContainer)}>
      <div className={classes.container}>
        <div className={classes.contentContainer}>
          {contentWithHighlights.suggestions && contentWithHighlights.suggestions.length > 0 && (
            <SearchItem
              title={t("searchRelated.suggestions")}
              content={contentWithHighlights.suggestions?.map((suggestion, index) =>
                createLinkItem(suggestion.url, suggestion.highlight, state, index, suggestion.term),
              )}
            />
          )}
          {contentWithHighlights.groups && contentWithHighlights.groups.length > 0 && (
            <SearchItem
              title={t("searchProduct.productGroups")}
              content={contentWithHighlights.groups?.map((group, index) =>
                createLinkItem(group.url, group.highlight, state, index, group.term ?? "", ContentTypes.Groups),
              )}
            />
          )}
          {contentWithHighlights.assets && contentWithHighlights.assets.length > 0 && (
            <SearchItem
              title={t("searchRelated.usefulAssets")}
              content={contentWithHighlights.assets
                ?.slice(0, dynamicMaxAssets)
                .map((asset, index) =>
                  createLinkItem(asset.label, asset.highlight, state, index, asset.term, ContentTypes.Assets),
                )}
            />
          )}
        </div>
        {isDesktop && products && products?.length > 0 && (
          <div className={classes.contentContainer}>
            <div className={classes.header}>{t("header.autosuggest.col.product")}</div>
            <div className={classes.productContainer} data-zoovu={"searchSuggestions"}>
              {products?.slice(0, MAX_PRODUCTS).map((item, index) => {
                if (state === "loading") {
                  return <Skeleton key={index} variant="rectangular" height={64} />;
                }

                return (
                  <ProductItem
                    key={index}
                    ref={(el) => {
                      if (el && !refList.current.includes(el)) {
                        refList.current.push(el);
                      }
                    }}
                    product={item}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className={classes.totalResults}>
        <Link
          href={SEARCH_PAGE + `?text=${query}`}
          iconBefore={<Icon type={IconType.Search} />}
          tracking-aux-sch-show-total-results-link="true"
        >
          {t("searchRelated.showAll")}
        </Link>
      </div>
    </div>
  );
};

export default SearchRelatedResults;
